import { useEffect, useMemo, useState } from 'react';

import { getNavigatorShareInterface } from '#src/app/shared/share/utils';

import type { ShareChannel } from '../shared/types';

import type { ShareLink } from './types';

// Channels gated by useIsMobileNativeShareCapable; filtered client-side after hydration when native share is unavailable.
const NATIVE_SHARE_GATED_CHANNELS = new Set<ShareChannel>(['mobileNativeShare', 'instagramStories', 'tiktok']);

/**
 * Filters `links` by removing channels that require native share support
 * (`mobileNativeShare`, `instagramStories`, `tiktok`) when that support is
 * absent on the current device. Starts with all links to avoid a hydration
 * mismatch, then drops the unsupported channels after mount. The disappear
 * only affects devices that lack the Web Share API, which is rare today.
 */
export function useNativeShareFilteredLinks<L extends Pick<ShareLink, 'channel'>>(links: readonly L[]): readonly L[] {
	const [isNativeShareCapable, setIsNativeShareCapable] = useState(true);

	useEffect(() => {
		if (!getNavigatorShareInterface()) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setIsNativeShareCapable(false);
		}
	}, []);

	return useMemo(
		() => (isNativeShareCapable ? links : links.filter((link) => !NATIVE_SHARE_GATED_CHANNELS.has(link.channel))),
		[links, isNativeShareCapable],
	);
}
